/**
 * B2BQuoteForm — modal der indsamler kundens volume + by og live-genererer
 * et AI-tilbud (Claude Sonnet via Emergent LLM key) + PDF + email på 10-15 sek.
 *
 * UX-flow:
 *   1. Form (4 felter) → submit
 *   2. Loader "AI bygger jeres tilbud..." med 3 trin (analyserer → udregner → bygger PDF)
 *   3. Poll /api/business/quote-status indtil quote_status='sent' (max 30s)
 *   4. Success-skærm med besparelses-tal + "Tjek din indbakke"
 */
import React from "react";
import { toast } from "sonner";
import { X, CheckCircle2, Send, Loader2, Sparkles, Mail, TrendingDown } from "lucide-react";
import { api } from "../lib/api";

const CITIES = [
  "København", "Frederiksberg", "Roskilde", "Køge", "Hillerød", "Næstved",
  "Helsingør", "Lyngby", "Glostrup", "Slagelse", "Holbæk", "Ringsted",
  "Greve", "Taastrup", "Albertslund", "Rødovre", "Andet på Sjælland",
];

const INDUSTRIES = [
  { v: "webshop", l: "Webshop / E-commerce" },
  { v: "advokat", l: "Advokat / Revisor" },
  { v: "klinik", l: "Klinik / Apotek" },
  { v: "restaurant", l: "Restaurant / Catering" },
  { v: "kontor", l: "Kontor / Bureau" },
  { v: "andet", l: "Andet" },
];

const empty = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  industry: "",
  pickup_city: "",
  parcels_per_day: 10,
  frequency: "daily",
  current_solution: "",
  notes: "",
};

const STEP_HINTS = [
  "Analyserer jeres volumen og by...",
  "Beregner rute-optimering og besparelser...",
  "Skriver jeres personlige tilbud...",
  "Bygger PDF og sender på email...",
];

export default function B2BQuoteForm({ open, onClose }) {
  const [form, setForm] = React.useState({ ...empty });
  const [phase, setPhase] = React.useState("form"); // form | loading | done | failed
  const [hintIdx, setHintIdx] = React.useState(0);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setForm({ ...empty });
      setPhase("form");
      setHintIdx(0);
      setResult(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => e.key === "Escape" && phase !== "loading" && onClose?.();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose, phase]);

  // Cycle through loader hints
  React.useEffect(() => {
    if (phase !== "loading") return undefined;
    const t = setInterval(() => setHintIdx((i) => (i + 1) % STEP_HINTS.length), 2500);
    return () => clearInterval(t);
  }, [phase]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.contact_email || !form.pickup_city || !form.parcels_per_day) {
      toast.error("Udfyld venligst de obligatoriske felter.");
      return;
    }
    setPhase("loading");
    setHintIdx(0);
    try {
      const { data } = await api.post("/business/quote-request", {
        ...form,
        parcels_per_day: Number(form.parcels_per_day),
      });
      const leadId = data.id;
      // Poll for completion
      let pollCount = 0;
      const poll = async () => {
        pollCount++;
        try {
          const { data: status } = await api.get(`/business/quote-status/${leadId}`);
          if (status.quote_status === "sent") {
            setResult({
              tier: status.quote_tier_name,
              km: status.quote_savings_km_month,
              hours: status.quote_savings_hours_month,
            });
            setPhase("done");
            return;
          }
          if (status.quote_status === "failed") {
            setPhase("failed");
            return;
          }
          if (pollCount < 30) {
            setTimeout(poll, 1500);
          } else {
            // Optimistic success after 45s — backend likely sent ok, email may take longer
            setPhase("done");
            setResult({ tier: "", km: null, hours: null });
          }
        } catch (err) {
          if (pollCount < 30) {
            setTimeout(poll, 1500);
          } else {
            setPhase("failed");
          }
        }
      };
      setTimeout(poll, 3500); // let background task get started
    } catch (err) {
      const msg = err?.response?.data?.detail || "Noget gik galt — prøv igen eller skriv til kontakt@emakurer.dk";
      toast.error(typeof msg === "string" ? msg : "Der opstod en fejl");
      setPhase("form");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6" data-testid="b2b-quote-modal">
      <div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" onClick={phase !== "loading" ? onClose : undefined} />
      <div className="relative bg-white border border-brand max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-brand-dark text-white px-6 py-5 flex items-center justify-between border-b border-white/10 z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-red flex items-center justify-center"><Sparkles size={18} /></div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">AI-skræddersyet</div>
              <div className="font-display font-semibold text-lg">Få et personligt tilbud på 60 sekunder</div>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={phase === "loading"}
            className="p-2 hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="b2b-quote-close"
            aria-label="Luk"
          >
            <X size={18} />
          </button>
        </div>

        {phase === "form" && (
          <form onSubmit={submit} className="p-6 space-y-5" data-testid="b2b-quote-form">
            <p className="text-brand-text2 text-sm leading-relaxed">
              Indtast jeres data — vi udregner straks et estimat på besparelse og sender et personligt tilbud (PDF) på email indenfor 1 minut.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Firmanavn *</label>
                <input className="input-flat" value={form.company_name} onChange={update("company_name")} data-testid="quote-company" required autoFocus />
              </div>
              <div>
                <label className="label-flat">Branche</label>
                <select className="input-flat" value={form.industry} onChange={update("industry")} data-testid="quote-industry">
                  <option value="">— Vælg —</option>
                  {INDUSTRIES.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Kontaktperson *</label>
                <input className="input-flat" value={form.contact_name} onChange={update("contact_name")} data-testid="quote-name" required />
              </div>
              <div>
                <label className="label-flat">Telefon</label>
                <input type="tel" className="input-flat" value={form.contact_phone} onChange={update("contact_phone")} data-testid="quote-phone" />
              </div>
            </div>

            <div>
              <label className="label-flat">Email *</label>
              <input type="email" className="input-flat" value={form.contact_email} onChange={update("contact_email")} data-testid="quote-email" required placeholder="vi sender tilbuddet direkte hertil" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Afhentnings-by *</label>
                <select className="input-flat" value={form.pickup_city} onChange={update("pickup_city")} data-testid="quote-city" required>
                  <option value="">— Vælg by —</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-flat">Antal pakker pr. dag *</label>
                <input
                  type="number"
                  min={1}
                  max={500}
                  className="input-flat"
                  value={form.parcels_per_day}
                  onChange={update("parcels_per_day")}
                  data-testid="quote-parcels"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Frekvens</label>
                <select className="input-flat" value={form.frequency} onChange={update("frequency")} data-testid="quote-frequency">
                  <option value="daily">Hver hverdag</option>
                  <option value="weekly">Et par gange om ugen</option>
                  <option value="adhoc">Ad-hoc</option>
                </select>
              </div>
              <div>
                <label className="label-flat">Nuværende løsning</label>
                <input
                  className="input-flat"
                  value={form.current_solution}
                  onChange={update("current_solution")}
                  data-testid="quote-current"
                  placeholder="GLS / PostNord / egen vogn..."
                />
              </div>
            </div>

            <div>
              <label className="label-flat">Bemærkninger (valgfri)</label>
              <textarea
                className="input-flat min-h-[80px]"
                value={form.notes}
                onChange={update("notes")}
                data-testid="quote-notes"
                placeholder="Særlige ønsker, leveringsområder, tidskrav..."
                rows={3}
              />
            </div>

            <div className="bg-brand-bg2 border-l-2 border-brand-red px-4 py-3 text-xs text-brand-text2 leading-relaxed">
              Vi gemmer dine oplysninger med henblik på opfølgning. Vi sender ingen marketing-mails. Se vores <a href="/handelsbetingelser#privacy" className="text-brand-red font-semibold underline">privatlivspolitik</a>.
            </div>

            <button type="submit" className="btn-primary w-full justify-center" data-testid="quote-submit">
              <Sparkles size={16} /> Generer mit tilbud
            </button>
          </form>
        )}

        {phase === "loading" && (
          <div className="px-6 py-16 text-center" data-testid="quote-loading">
            <div className="w-16 h-16 mx-auto bg-brand-dark text-white flex items-center justify-center mb-6">
              <Loader2 size={28} className="animate-spin" />
            </div>
            <h3 className="font-display font-semibold text-2xl text-brand-dark">Vores AI bygger jeres tilbud</h3>
            <p className="text-brand-text2 mt-4 max-w-md mx-auto leading-relaxed" key={hintIdx} style={{ animation: "fadeIn 300ms" }}>
              {STEP_HINTS[hintIdx]}
            </p>
            <div className="mt-8 grid grid-cols-4 gap-2 max-w-md mx-auto">
              {STEP_HINTS.map((_, i) => (
                <div
                  key={i}
                  className="h-1 rounded-full"
                  style={{
                    background: i <= hintIdx ? "#DC2626" : "#E2E8F0",
                    transition: "background 300ms",
                  }}
                />
              ))}
            </div>
            <p className="text-brand-text2/70 text-xs mt-8 max-w-sm mx-auto">
              Tager typisk 10-20 sek. Du modtager også tilbuddet direkte på email om lidt.
            </p>
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: translateY(0) } }`}</style>
          </div>
        )}

        {phase === "done" && (
          <div className="px-6 py-12 text-center" data-testid="quote-done">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="font-display font-semibold text-2xl text-brand-dark mt-5">Tilbuddet er sendt til {form.contact_email}</h3>
            {result && result.km > 0 && (
              <div className="mt-6 bg-brand-dark text-white p-5 mx-auto max-w-md text-left">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-red flex items-center gap-2">
                  <TrendingDown size={12} /> Jeres estimerede besparelse pr. md
                </div>
                <div className="font-display font-black text-2xl mt-2">
                  ~{Math.round(result.km).toLocaleString("da-DK")} km · {Math.round(result.hours)} timer sparet
                </div>
                {result.tier && (
                  <div className="text-xs text-white/70 mt-2">Anbefalet løsning: <strong className="text-white">{result.tier}-pakken</strong></div>
                )}
              </div>
            )}
            <p className="text-brand-text2 mt-6 max-w-md mx-auto leading-relaxed">
              Tjek din indbakke — vi har vedhæftet det fulde tilbud som PDF. Vores team tager fat i jer indenfor 1 hverdag.
            </p>
            <button onClick={onClose} className="btn-secondary mt-8" data-testid="quote-close-success">Luk</button>
          </div>
        )}

        {phase === "failed" && (
          <div className="px-6 py-12 text-center" data-testid="quote-failed">
            <div className="w-16 h-16 bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
              <Mail size={28} />
            </div>
            <h3 className="font-display font-semibold text-2xl text-brand-dark mt-5">Vi vender personligt tilbage</h3>
            <p className="text-brand-text2 mt-4 max-w-md mx-auto leading-relaxed">
              AI-tilbuddet kom ikke igennem lige nu, men din henvendelse er noteret. Vores team kontakter dig på <strong className="text-brand-dark">{form.contact_email}</strong> indenfor 1 hverdag.
            </p>
            <button onClick={onClose} className="btn-secondary mt-8" data-testid="quote-close-failed">Luk</button>
          </div>
        )}
      </div>
    </div>
  );
}
